import PropTypes from 'prop-types';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { connect, useSelector } from 'react-redux';
import cx from 'classnames';
import I18n from 'i18n-js';
import NewBookingDuck from 'ducks/new-booking-duck';
import ScribeDuck from 'ducks/scribe-duck';
import WithTooltip from 'components/v2/with-tooltip-hoc';
import { ExceptMobile, Mobile } from 'components/v2/responsive';
import { Button } from 'grab-ui';
import { isEmptyAllDeliveries } from 'helper/anytime-booking';
import {
  allDeliveriesSelector,
  bookingConfigurationSelector,
} from 'selectors/new-booking-selectors';
import useUnloadConfirm from 'components/v2/anytime-booking/common/use-unload-confirm';

import DeliveryForm from './delivery-form';
import QuotePanel from './quote-panel';
import styles from './styles.less';

const NewBookingPage = ({ dispatch, allDeliveries }) => {
  const bookingConfiguration = useSelector(bookingConfigurationSelector);

  const createNewDelivery = useCallback(
    () => {
      dispatch(NewBookingDuck.creators.createNewDelivery(bookingConfiguration));
    },
    [dispatch, bookingConfiguration],
  );

  const createNewDeliveryPositionClick = useCallback(
    position => () => {
      if (position === 'left') {
        dispatch(
          ScribeDuck.creators.scribeExpressDeliveryDetailAddDeliveryLeft(),
        );
      }
      if (position === 'bottom') {
        dispatch(
          ScribeDuck.creators.scribeExpressDeliveryDetailAddDeliveryBottom(),
        );
      }
      createNewDelivery();
    },
    [createNewDelivery, dispatch],
  );

  const onClickDeliveryLeftBar = useCallback(
    index => () => {
      dispatch(ScribeDuck.creators.scribeExpressDeliveryDetailLeftBar(index));
    },
    [createNewDelivery],
  );

  useEffect(
    () => {
      if (allDeliveries.length === 0 && bookingConfiguration) {
        createNewDelivery();
      }
    },
    [createNewDelivery, bookingConfiguration, allDeliveries],
  );

  const allDeliveriesEmpty = useMemo(
    () => isEmptyAllDeliveries(allDeliveries),
    [allDeliveries],
  );
  useUnloadConfirm(allDeliveriesEmpty);

  const formRefs = useRef({});
  const setFormRef = useCallback(
    deliveryID => ref => {
      formRefs.current[deliveryID] = ref;
    },
    [formRefs],
  );

  const { allowAdd, reason: notAllowAddReason } = useMemo(
    () => ({
      allowAdd: true,
      reason: '',
    }),
    [],
  );

  return (
    <div className={styles.newBookingPage}>
      <div className={styles.floatingIndexes}>
        <div className={styles.indexes}>
          {allDeliveries.map((delivery, index) => (
            <a
              key={delivery.id}
              className={styles.index}
              onClick={onClickDeliveryLeftBar(index)}
            >
              {index + 1}
            </a>
          ))}
        </div>
        <WithTooltip
          hasTooltip={!allowAdd}
          title={notAllowAddReason}
          placement={'bottom'}
        >
          <Button
            disabled={!allowAdd}
            className={cx(styles.addNew, { [styles.disabledAdd]: !allowAdd })}
            onClick={createNewDeliveryPositionClick('left')}
          >
            +
          </Button>
        </WithTooltip>
      </div>
      <div className={styles.pageContent}>
        <div className={styles.deliveryForms}>
          {allDeliveries.map((delivery, index) => (
            <DeliveryForm
              wrappedComponentRef={setFormRef(delivery.id)}
              // must have version, to re-create form when ro happens
              key={`${delivery.id}-${delivery.version}`}
              index={index}
              deliveryID={delivery.id}
              canDelete={allDeliveries.length > 1}
              canCopy={allowAdd}
            />
          ))}
          <WithTooltip
            hasTooltip={!allowAdd}
            title={notAllowAddReason}
            placement={'bottom'}
          >
            <Button
              className={cx(styles.addNewDeliveryButton, {
                [styles.disabledAdd]: !allowAdd,
              })}
              disabled={!allowAdd}
              onClick={createNewDeliveryPositionClick('bottom')}
            >
              {`+ ${I18n.t('v2.homepage.manualOrder.button.addNewDelivery')}`}
            </Button>
          </WithTooltip>
        </div>
        <ExceptMobile>
          <div className={styles.quotePanelBlock}>
            <QuotePanel formRefs={formRefs.current} />
          </div>
        </ExceptMobile>
      </div>
      <Mobile>
        <div className={styles.quotePanelBlockMobile}>
          <QuotePanel formRefs={formRefs.current} />
        </div>
      </Mobile>
    </div>
  );
};

NewBookingPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  allDeliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
};

export default connect(state => ({
  allDeliveries: allDeliveriesSelector(state),
}))(NewBookingPage);
